/**
 * TargetPriceDraftSheet — bottom-anchored builder for the live target-price
 * card (spec §11). ThesisDraftSheet's twin: a plain RN Modal, per-field commit
 * through onEdit, close + gated actions in the footer.
 *
 * The builder is a TOOL, not advice: every driver edit marks its key in the
 * parent's `confirmed` map (provenance), and Compute arms only via the
 * engagement gate `computeEnabled` — by VALUE PRESENCE since 2026-10-17
 * (option C), and since tp-wizard task 12 ALSO on a fully confirmed wizard
 * build when the transcript's `confirmedKeys` are threaded in (review mode).
 * The folded "Advanced / assumptions"
 * block is pre-filled from the card and NOT gated. The output panel renders
 * only after a compute round-trip, under the literal "your inputs" framing
 * plus the standard disclaimer. AI accept-or-edit chips are NOT rendered
 * here — they arrive as conversation QuestionChips (Task 12).
 *
 * PROPS CONTRACT (Task 12 wiring):
 * - onEdit(fields, confirmKey): `fields` is a Partial<TargetPriceCard> patch.
 *   A scenario-driver edit carries that scenario's full dict —
 *   `{ bull: { ...card.bull, wacc: 0.15 } }` — its established wire shape;
 *   applyEdit merges scenario patches per driver, so the siblings riding along
 *   are no-ops. Probabilities and advanced fields patch bare keys. `confirmKey` is
 *   a driver key ("<scenario>.<driver>" or bare "bull_prob"/…) for every
 *   PRIMARY-driver commit that lands a VALUE, and explicitly `undefined` for
 *   advanced (non-gated) fields and for a commit that clears a value (nothing
 *   to confirm). When defined the parent merges `fields` optimistically and
 *   sets `confirmed[confirmKey] = true` ONLY once the edit POST succeeds — a
 *   confirmation must never survive a rejected edit (review #2), or the gate
 *   and the §15 provenance would count a value that never persisted.
 * - (2026-10-17) the sheet's own ✓ affordance is REMOVED (issue 2, option C):
 *   compute is armed by value presence and the single footer press is the
 *   engagement act. Edits still mark `confirmed[key]` (provenance 'user' for
 *   the save payload;
 *   see provenanceOf). The disabled-on-null rule moved with the affordance to
 *   the bubble's accept chips (final-review I2): a row whose value is null
 *   renders its chip DISABLED — confirming an
 *   absent number is not a thing (the old review #3a note).
 * - confirmed: the client engagement map — provenance input (provenanceOf)
 *   since option C; the sheet never mutates it directly.
 * - confirmedKeys (tp-wizard PR-B task 12, review mode): the SERVER's
 *   confirmed-key set — the screen threads the newest transcript page's
 *   `target_price_state.confirmed` in (B3; it used to fold the messages it had
 *   paged, a 20-row window against the server's 200). When present, compute
 *   arms with the confirmation-armed
 *   `computeEnabled(card, confirmedKeys)` (every wizard stage confirmed —
 *   currentStageClient null); ABSENT keeps the option-C value-presence
 *   behaviour. The footer's disabled state carries the `pendingStages` hint
 *   ONLY in the gap state — values complete + probs summed (the old gate
 *   armed) while stages remain unconfirmed.
 * - computed: the POST .../target-price-compute result (prices, weighted,
 *   sensitivity). The output panel renders iff non-null; Save arms via
 *   saveEnabled(computed).
 * - onCompute / onSave: parent POSTs and reflects progress through the
 *   computing / saving flags, which lock every input and button. Both receive
 *   the driver rows the press had to flush first (see `SheetFlushedRow`) and
 *   MUST fold them into the card they POST — the flush's own commits land
 *   asynchronously, so the parent's card state is still the pre-flush one in
 *   the same tick.
 *
 * Inputs keep local drafts, re-seeded when the sheet opens: the card prop
 * round-trips through the server, so binding inputs straight to it would fire
 * a POST per keystroke (same rule as ThesisDraftSheet — last-writer-wins).
 * Percent fields (growth, margins, wacc/tg, probabilities, tax rate) render
 * percent views of the card's decimals and divide back on commit; junk inputs
 * are ignored, keeping the last good value.
 *
 * SPEC §2 (one renderer, two surfaces): the scenario-driver and probability
 * rows are the shared ScenarioInputGroup — the wizard bubble renders the same
 * rows, so the two surfaces cannot disagree on values, formatting, or commit
 * semantics. The sheet maps its groups into `rows` (labels via i18n here) and
 * keeps the advanced block, footer, output panel, and header rationale prose.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScenarioInputGroup,
  numToDraft,
  pctToDraft,
  getTpRowStyles,
} from './ScenarioInputGroup';
import type { ScenarioInputRow, ScenarioInputGroupHandle } from './ScenarioInputGroup';
import {
  computeEnabled,
  isScenarioComputable,
  saveEnabled,
  PRIMARY_CONFIRM_KEYS,
} from '../../services/targetPriceCard';
import type {
  ScenarioDrivers,
  ScenarioKey,
  TargetPriceCard,
  TargetPriceComputed,
} from '../../services/targetPriceCard';

const SCENARIOS: { key: ScenarioKey; labelKey: string; defaultLabel: string }[] = [
  { key: 'bull', labelKey: 'chat.targetPrice.bull', defaultLabel: 'Bull' },
  { key: 'base', labelKey: 'chat.targetPrice.base', defaultLabel: 'Base' },
  { key: 'bear', labelKey: 'chat.targetPrice.bear', defaultLabel: 'Bear' },
];

const SCENARIO_DRIVERS: {
  field: keyof ScenarioDrivers;
  labelKey: string;
  defaultLabel: string;
  percent: boolean;
}[] = [
  {
    field: 'revenue_growth',
    labelKey: 'chat.targetPrice.revenueGrowth',
    defaultLabel: 'Revenue growth (CAGR %)',
    percent: true,
  },
  {
    field: 'ebit_margin',
    labelKey: 'chat.targetPrice.ebitMargin',
    defaultLabel: 'EBIT margin %',
    percent: true,
  },
  { field: 'wacc', labelKey: 'chat.targetPrice.wacc', defaultLabel: 'WACC %', percent: true },
  {
    field: 'terminal_growth',
    labelKey: 'chat.targetPrice.terminalGrowth',
    defaultLabel: 'Terminal growth %',
    percent: true,
  },
  {
    field: 'base_revenue',
    labelKey: 'chat.targetPrice.baseRevenue',
    defaultLabel: 'Base revenue',
    percent: false,
  },
];

const PROB_ROWS = [
  { key: 'bull_prob', labelKey: 'chat.targetPrice.bullProb', defaultLabel: 'Bull probability %' },
  { key: 'base_prob', labelKey: 'chat.targetPrice.baseProb', defaultLabel: 'Base probability %' },
  { key: 'bear_prob', labelKey: 'chat.targetPrice.bearProb', defaultLabel: 'Bear probability %' },
] as const;

// Card fields patched by a bare key (probabilities + advanced numerics); the
// scenario dicts (merged per driver) and the two never-edited fields — symbol
// and currency — are excluded here. Currency is excluded because it is not an
// input at all: the server derives it from the reporting currency of the
// fundamentals, so an editable row would only let the user label a USD fold
// "HKD" (0100.HK). The sheet shows it beside the computed figures instead.
// Constraining the bare-patch key to this union is what lets the patch build
// type-check without a cast — a mistyped prob key no longer compiles.
type BarePatchKey = Exclude<keyof TargetPriceCard, ScenarioKey | 'symbol' | 'currency'>;

// The domain has no D&A/capex/ΔNWC fields — the folded block shows ONLY the
// assumption fields TargetPriceCard actually carries (intentional cut of the
// plan's aspirational row list to the domain's truth).
type AdvancedNumberRow = {
  key: BarePatchKey;
  labelKey: string;
  defaultLabel: string;
  percent?: boolean;
  int?: boolean;
};

const ADVANCED_ROWS: AdvancedNumberRow[] = [
  {
    key: 'tax_rate',
    labelKey: 'chat.targetPrice.taxRate',
    defaultLabel: 'Tax rate %',
    percent: true,
  },
  {
    key: 'revenue_unit',
    labelKey: 'chat.targetPrice.revenueUnit',
    defaultLabel: 'Revenue unit (e.g. 1,000,000)',
  },
  { key: 'net_cash', labelKey: 'chat.targetPrice.netCash', defaultLabel: 'Net cash / (debt)' },
  {
    key: 'shares_outstanding',
    labelKey: 'chat.targetPrice.sharesOutstanding',
    defaultLabel: 'Shares outstanding',
  },
  {
    key: 'horizon_years',
    labelKey: 'chat.targetPrice.horizon',
    defaultLabel: 'Forecast horizon (years)',
    int: true,
  },
];

// Derived from the domain's canonical confirm-key list — the same keys
// computeEnabled/provenanceOf check, so the sheet's gating metadata can never
// drift from the gate itself.
const PRIMARY_ROW_KEYS = new Set<string>(PRIMARY_CONFIRM_KEYS);

// Only the advanced block's drafts live here now — the scenario/probability
// drafts moved into ScenarioInputGroup (re-seeded by remounting the groups on
// open; see the key flip in the render).
function seedDrafts(card: TargetPriceCard | null): Record<string, string> {
  const d: Record<string, string> = {};
  if (!card) return d;
  for (const a of ADVANCED_ROWS) {
    const v = card[a.key];
    d[a.key as string] = a.percent ? pctToDraft(v as number) : numToDraft(v as number);
  }
  return d;
}

/** Header/cell view of a stringified-float sensitivity key, e.g. "0.1" → "10%". */
function pctHeader(key: string): string {
  const n = Number(key);
  return Number.isFinite(n) ? `${Number((n * 100).toPrecision(6))}%` : key;
}

/** A driver row the user typed into but never blurred, flushed by a footer
 * action. Structurally the bubble's `FlushedRow`: value in CARD units (decimal,
 * never the ×100 percent view), null = cleared. */
type SheetFlushedRow = { key: string; value: number | null };

export function TargetPriceDraftSheet({
  visible,
  card,
  confirmed,
  confirmedKeys,
  computed,
  onClose,
  onEdit,
  onCompute,
  onSave,
  computing,
  saving,
}: {
  visible: boolean;
  card: TargetPriceCard | null;
  confirmed: Record<string, boolean>;
  /** The SERVER's confirmed stage keys (`target_price_state.confirmed`, B3);
   * optional — absent = the pre-task-12 value-presence arming. */
  confirmedKeys?: Set<string>;
  computed?: TargetPriceComputed | null;
  onClose: () => void;
  onEdit: (fields: Partial<TargetPriceCard>, confirmKey?: string) => void;
  onCompute: (flushed: SheetFlushedRow[]) => void;
  onSave: (flushed: SheetFlushedRow[]) => void;
  computing: boolean;
  saving: boolean;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Local drafts for the free-typed fields; re-seeded each time the sheet
  // opens (deliberately not while open — see the file comment). The advanced
  // block resets to its default-collapsed state on every open.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  useEffect(() => {
    if (!visible) return;
    setDrafts(seedDrafts(card));
    setAdvancedOpen(false);
    // Re-seed only on open/close: the card prop round-trips through the
    // server, so re-seeding while open would clobber in-flight local drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const locked = saving || computing;
  // Review-mode arming (task 12): `canCompute` is the confirmation-armed gate
  // when the transcript's confirmed keys were threaded in, else the option-C
  // value-presence gate. `valuesArmed` is ALWAYS the one-arg (value-presence)
  // gate — the hint shows only in the gap between the two: values the fold
  // accepts while wizard stages are still unconfirmed.
  const canCompute = card ? computeEnabled(card, confirmedKeys) : false;
  const valuesArmed = card ? computeEnabled(card) : false;
  const pendingStages = valuesArmed && !canCompute;
  const canSave = saveEnabled(computed);

  // The four driver groups (three scenarios + probabilities), by group name.
  // A footer act flushes them before it fires: `keyboardShouldPersistTaps`
  // keeps the focused row from blurring, so its draft never reaches
  // `onCommit`, and the parent cannot read this tick's optimistic card update
  // back out of React state — hence the values are reported, not just
  // committed (2026-10-29 review I-2).
  // Residual, accepted: the ADVANCED block's own drafts (tax rate, shares,
  // net cash, horizon) still commit on blur only. Folding them too
  // would mean re-deriving `commitBareNumber`'s percent/int parsing here, i.e.
  // a second definition of a commit — worse than the gap.
  const groupRefs = useRef<Record<string, ScenarioInputGroupHandle | null>>({});
  const flushGroups = (): SheetFlushedRow[] => {
    const flushed: SheetFlushedRow[] = [];
    Object.values(groupRefs.current).forEach((handle) => {
      handle?.flushDirty().forEach(({ key, value }) => {
        flushed.push({ key, value: value.trim() === '' ? null : Number(value) });
      });
    });
    return flushed;
  };

  // The scenario-group commits receive raw strings already in CARD units —
  // the ×100 divide-back lives in ScenarioInputGroup (one renderer, §2).
  const commitScenarioNumber = (scenario: ScenarioKey, field: keyof ScenarioDrivers, raw: string) => {
    if (!card) return;
    const s = raw.trim();
    let value: number | null;
    if (!s) {
      value = null;
    } else {
      const n = Number(s);
      if (!Number.isFinite(n)) return; // ignore junk; keep the last good value
      value = n;
    }
    // Never materialize a scenario the user just emptied: an all-null dict
    // would turn "(not part of this build)" into a present-but-incomputable
    // scenario (review #3b) and change the bar's fill count with nothing in it.
    if (value === null && card[scenario] == null) return;
    // The sheet's wire shape is the full scenario dict; applyEdit merges per
    // driver, so the siblings riding along are inert.
    const prev: Partial<ScenarioDrivers> = card[scenario] ?? {};
    const fields = { [scenario]: { ...prev, [field]: value } } as Partial<TargetPriceCard>;
    const key = `${scenario}.${field}`;
    // A null value has nothing to confirm: "using my value" would be a claim
    // about an absent number (review #3b).
    onEdit(fields, value !== null && PRIMARY_ROW_KEYS.has(key) ? key : undefined);
  };

  const commitBareNumber = (
    key: BarePatchKey,
    raw: string,
    opts: { percent?: boolean; int?: boolean }
  ) => {
    if (!card) return;
    const s = raw.trim();
    let value: number | null;
    if (!s) {
      value = null;
    } else {
      const n = Number(s);
      if (!Number.isFinite(n)) return; // ignore junk; keep the last good value
      value = opts.percent ? n / 100 : opts.int ? Math.trunc(n) : n;
    }
    // Every BarePatchKey is `number | null` on the card, so the typed indexed
    // write needs no cast — a key outside the union won't compile.
    const fields: Partial<TargetPriceCard> = {};
    fields[key] = value;
    // Same rule as commitScenarioNumber: clearing a probability confirms
    // nothing (review #3b). Advanced rows are not in PRIMARY_ROW_KEYS anyway.
    onEdit(fields, value !== null && PRIMARY_ROW_KEYS.has(key) ? key : undefined);
  };

  // The scenario/probability rows render through the shared ScenarioInputGroup;
  // this local renderer remains only for the folded advanced block (whose int
  // commits don't fit the rows interface).
  const advancedRow = (row: {
    testKey: string;
    label: string;
    percentInput?: boolean;
    commit: (raw: string) => void;
  }) => {
    return (
      <View key={row.testKey} style={styles.driverRow}>
        <View style={styles.driverMain}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>{row.label}</Text>
        </View>
        <TextInput
          testID={`tp-advanced-${row.testKey}`}
          style={[
            styles.input,
            {
              backgroundColor: theme.colors.backgroundElevated,
              borderColor: theme.colors.border,
              color: theme.colors.text,
            },
          ]}
          value={drafts[row.testKey] ?? ''}
          onChangeText={(v) => setDrafts((d) => ({ ...d, [row.testKey]: v }))}
          onEndEditing={() => row.commit(drafts[row.testKey] ?? '')}
          keyboardType={row.percentInput ? 'decimal-pad' : 'numbers-and-punctuation'}
          placeholder="0.00"
          placeholderTextColor={theme.colors.textTertiary}
          editable={!locked}
        />
        {/* 2026-10-17 (issue 2, option C): the per-driver ✓ confirm button is
            GONE — one 確認並計算 press in the footer is the engagement act.
            The spacer keeps the row geometry stable. */}
        <View style={styles.confirmSpacer} />
      </View>
    );
  };

  // The label rides the FOLD, not the card: `computed.currency` is the
  // reporting currency the server derived and every figure beside it is
  // denominated in. `card.currency` is only the fallback for a fold that
  // predates it (0100.HK: an HKD label sitting on USD figures).
  const fmtPrice = (v: number | null | undefined): string => {
    if (v == null || !Number.isFinite(v)) return '—';
    const ccy = computed?.currency || card?.currency;
    return ccy ? `${v.toFixed(2)} ${ccy}` : v.toFixed(2);
  };

  const sensitivityRows = useMemo(() => {
    const grid = computed?.sensitivity;
    if (!grid || !Object.keys(grid).length) return null;
    const waccs = Object.keys(grid).sort((a, b) => Number(a) - Number(b));
    const tgs = Array.from(new Set(Object.values(grid).flatMap((row) => Object.keys(row)))).sort(
      (a, b) => Number(a) - Number(b)
    );
    return { waccs, tgs };
  }, [computed]);

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close', { defaultValue: 'Close' })}
        />
        <View style={[styles.sheet, { backgroundColor: theme.colors.surface }]}>
          <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />
          {card && (
            <>
              <Text style={[styles.title, { color: theme.colors.text }]}>
                {t('chat.targetPrice.draft', { defaultValue: 'Target price draft' })} ·{' '}
                {card.symbol}
              </Text>

              <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
              >
                {/* Primary drivers — one group per scenario, then probabilities.
                    Rows render through the shared ScenarioInputGroup (§2); the
                    key flip remounts the groups on open so their drafts re-seed
                    from the card (the sheet's long-standing open contract). */}
                {SCENARIOS.map((s) => {
                  const groupRows: ScenarioInputRow[] = SCENARIO_DRIVERS.map((d) => ({
                    key: `${s.key}.${d.field}`,
                    label: t(d.labelKey, { defaultValue: d.defaultLabel }),
                    value: card[s.key]?.[d.field] ?? null,
                    percent: d.percent,
                  }));
                  return (
                    <View key={s.key}>
                      {/* The server's compute fold skips a scenario unless ALL
                          five drivers are present, so an incomplete scenario is
                          labelled inactive exactly like an absent one — the sheet
                          must not imply a number the fold will never produce. */}
                      <Text style={[styles.groupTitle, { color: theme.colors.text }]}>
                        {t(s.labelKey, { defaultValue: s.defaultLabel })}
                        {!isScenarioComputable(card[s.key])
                          ? ` ${t('chat.targetPrice.emptyScenario', {
                              defaultValue: '(not part of this build)',
                            })}`
                          : ''}
                      </Text>
                      {/* 2026-10-17 (issue 1): the agent's per-scenario
                          suggestion text — rendered directly under the scenario
                          header so the "why these numbers" travels with the
                          numbers, not in a distant chat bubble. */}
                      {!!card.rationale?.[s.key] && (
                        <Text
                          testID={`tp-rationale-${s.key}`}
                          style={[
                            styles.rationaleText,
                            { color: theme.colors.textSecondary },
                          ]}
                        >
                          {card.rationale[s.key]}
                        </Text>
                      )}
                      <ScenarioInputGroup
                        key={`${s.key}-${visible ? 'open' : 'closed'}`}
                        ref={(h) => {
                          groupRefs.current[s.key] = h;
                        }}
                        mode="sheet"
                        editable={!locked}
                        testIDPrefix="tp-input"
                        rows={groupRows}
                        onCommit={(rowKey, raw) => {
                          const d = SCENARIO_DRIVERS.find(
                            (x) => `${s.key}.${x.field}` === rowKey
                          );
                          if (d) commitScenarioNumber(s.key, d.field, raw);
                        }}
                      />
                    </View>
                  );
                })}
                <Text style={[styles.groupTitle, { color: theme.colors.text }]}>
                  {t('chat.targetPrice.probabilities', { defaultValue: 'Probabilities' })}
                </Text>
                <ScenarioInputGroup
                  key={`probs-${visible ? 'open' : 'closed'}`}
                  ref={(h) => {
                    groupRefs.current.probs = h;
                  }}
                  mode="sheet"
                  editable={!locked}
                  testIDPrefix="tp-input"
                  rows={PROB_ROWS.map((p) => ({
                    key: p.key,
                    label: t(p.labelKey, { defaultValue: p.defaultLabel }),
                    value: card[p.key] ?? null,
                    percent: true,
                  }))}
                  onCommit={(rowKey, raw) => {
                    const p = PROB_ROWS.find((x) => x.key === rowKey);
                    if (p) commitBareNumber(p.key, raw, {});
                  }}
                />

                {/* Advanced / assumptions — folded by default, NOT gated. */}
                <TouchableOpacity
                  testID="tp-advanced-toggle"
                  style={[styles.advancedToggle, { borderColor: theme.colors.border }]}
                  onPress={() => setAdvancedOpen((o) => !o)}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: advancedOpen }}
                  accessibilityLabel={t('chat.targetPrice.advanced', {
                    defaultValue: 'Advanced / assumptions',
                  })}
                >
                  <Text style={[styles.advancedToggleText, { color: theme.colors.textSecondary }]}>
                    {advancedOpen ? '▾' : '▸'}{' '}
                    {t('chat.targetPrice.advanced', { defaultValue: 'Advanced / assumptions' })}
                  </Text>
                </TouchableOpacity>
                {advancedOpen &&
                  ADVANCED_ROWS.map((a) =>
                    advancedRow({
                      testKey: a.key,
                      label: t(a.labelKey, { defaultValue: a.defaultLabel }),
                      percentInput: a.percent,
                      commit: (raw) =>
                        commitBareNumber(a.key, raw, { percent: a.percent, int: a.int }),
                    })
                  )}

                {/* Output panel — only after a compute round-trip (spec §12):
                    the numbers are derived, so they never render pre-compute. */}
                {computed && (
                  <View
                    testID="tp-output"
                    style={[
                      styles.output,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                      },
                    ]}
                  >
                    <Text style={[styles.outputTitle, { color: theme.colors.text }]}>
                      {t('chat.targetPrice.outputLabel', {
                        defaultValue: 'Your scenario values, from your inputs.',
                      })}
                    </Text>
                    {SCENARIOS.map((s) => (
                      <View key={s.key} style={styles.outputRow}>
                        <Text style={[styles.outputLabel, { color: theme.colors.textSecondary }]}>
                          {t(s.labelKey, { defaultValue: s.defaultLabel })}{' '}
                          {t('chat.targetPrice.price', { defaultValue: 'price' })}
                        </Text>
                        <Text
                          testID={`tp-price-${s.key}`}
                          style={[styles.outputValue, { color: theme.colors.text }]}
                        >
                          {fmtPrice(computed.prices?.[s.key])}
                        </Text>
                      </View>
                    ))}
                    <View style={styles.outputRow}>
                      <Text style={[styles.outputLabel, { color: theme.colors.textSecondary }]}>
                        {t('chat.targetPrice.weightedTarget', { defaultValue: 'Weighted target' })}
                      </Text>
                      <Text
                        testID="tp-weighted"
                        style={[styles.weighted, { color: theme.colors.primary }]}
                      >
                        {fmtPrice(computed.weighted)}
                      </Text>
                    </View>
                    {sensitivityRows && (
                      <View testID="tp-sensitivity" style={styles.grid}>
                        <Text style={[styles.gridTitle, { color: theme.colors.textSecondary }]}>
                          {t('chat.targetPrice.sensitivityTitle', {
                            defaultValue: 'Sensitivity — WACC × terminal growth',
                          })}
                        </Text>
                        <View style={styles.gridRow}>
                          <View style={styles.gridCorner} />
                          {sensitivityRows.tgs.map((tg) => (
                            <Text
                              key={`head-tg-${tg}`}
                              testID={`tp-sens-head-tg-${tg}`}
                              style={[styles.gridHead, { color: theme.colors.textSecondary }]}
                            >
                              {pctHeader(tg)}
                            </Text>
                          ))}
                        </View>
                        {sensitivityRows.waccs.map((w) => (
                          <View key={`row-${w}`} style={styles.gridRow}>
                            <Text
                              testID={`tp-sens-head-wacc-${w}`}
                              style={[styles.gridHead, { color: theme.colors.textSecondary }]}
                            >
                              {pctHeader(w)}
                            </Text>
                            {sensitivityRows.tgs.map((tg) => {
                              const v = computed.sensitivity?.[w]?.[tg];
                              return (
                                <Text
                                  key={`cell-${w}-${tg}`}
                                  testID={`tp-sens-${w}-${tg}`}
                                  style={[styles.gridCell, { color: theme.colors.text }]}
                                >
                                  {v != null && Number.isFinite(v) ? v.toFixed(2) : '—'}
                                </Text>
                              );
                            })}
                          </View>
                        ))}
                      </View>
                    )}
                    <Text style={[styles.disclaimer, { color: theme.colors.textTertiary }]}>
                      {t('common.advisoryDisclaimer', {
                        defaultValue: 'Educational information only — not personalized advice.',
                      })}
                    </Text>
                  </View>
                )}
              </ScrollView>

              {/* Footer: close + engagement-gated Compute + compute-gated Save.
                  The pendingStages hint sits above the row ONLY in the gap
                  state (task 12): the values would arm the old gate but wizard
                  stages are still unconfirmed. The 「全部確認」 act that used to
                  sit beside it is gone (B3, 2026-10-31 staging 0100.HK) — it
                  recovered from the client's own short-window fold disagreeing
                  with the server's, and with the fold server-authoritative it
                  had nothing left to recover. */}
              <View style={styles.footerWrap}>
                {pendingStages && (
                  <Text
                    testID="tp-pending-stages"
                    style={[styles.pendingStages, { color: theme.colors.textTertiary }]}
                  >
                    {t('chat.targetPrice.pendingStages', {
                      defaultValue: 'Stages left to confirm',
                    })}
                  </Text>
                )}
                <View style={styles.footer}>
                  <TouchableOpacity
                    testID="tp-sheet-close"
                    style={[styles.closeButton, { borderColor: theme.colors.border }]}
                    onPress={onClose}
                    disabled={locked}
                  >
                    <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>
                      ✕ {t('common.close', { defaultValue: 'Close' })}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="tp-compute"
                    style={[
                      styles.footerButton,
                      { backgroundColor: theme.colors.primary },
                      (!canCompute || computing) && styles.actionDisabled,
                    ]}
                    onPress={() => onCompute(flushGroups())}
                    disabled={!canCompute || computing}
                    accessibilityRole="button"
                    accessibilityState={{ disabled: !canCompute || computing }}
                  >
                    <Text style={[styles.footerButtonText, { color: theme.colors.textInverse }]}>
                      {computing
                        ? t('chat.targetPrice.computing', { defaultValue: 'Computing...' })
                        : t('chat.targetPrice.confirmCompute', {
                            defaultValue: '確認並計算 / Confirm & Calculate',
                          })}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="tp-save"
                    style={[
                      styles.footerButton,
                      { backgroundColor: theme.colors.primary },
                      (!canSave || saving) && styles.actionDisabled,
                    ]}
                    onPress={() => onSave(flushGroups())}
                    disabled={!canSave || saving}
                    accessibilityRole="button"
                    accessibilityState={{ disabled: !canSave || saving }}
                  >
                    <Text style={[styles.footerButtonText, { color: theme.colors.textInverse }]}>
                      {saving
                        ? t('common.saving', { defaultValue: 'Saving...' })
                        : t('chat.targetPrice.save', { defaultValue: 'Save target price' })}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

function createThemedStyles(theme: any) {
  // Row styles are the shared renderer's cached singletons (spec §2): the
  // sheet and ScenarioInputGroup render from the SAME style objects, so row
  // geometry/colors cannot drift between the two surfaces.
  const rowStyles = getTpRowStyles(theme, 'sheet');
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      justifyContent: 'flex-end',
      backgroundColor: theme.colors.overlay,
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
    },
    sheet: {
      maxHeight: '90%',
      borderTopLeftRadius: theme.radii.lg,
      borderTopRightRadius: theme.radii.lg,
      paddingTop: theme.spacing.xs,
      paddingBottom: theme.spacing.lg,
    },
    handle: {
      alignSelf: 'center',
      width: 40,
      height: 4,
      borderRadius: theme.radii.full,
      marginBottom: theme.spacing.sm,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      paddingHorizontal: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
    },
    // Shrink below content height so the sheet respects its maxHeight cap.
    scroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    scrollContent: {
      paddingHorizontal: theme.spacing.lg,
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.md,
    },
    rationaleText: {
      // De-emphasized suggestion prose under each scenario header (issue 1).
      fontSize: theme.typography.fontSizeXs,
      lineHeight: theme.typography.fontSizeXs * 1.5,
      marginBottom: theme.spacing.xs,
    },
    groupTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.xxs,
    },
    label: rowStyles.label,
    driverRow: rowStyles.driverRow,
    driverMain: rowStyles.driverMain,
    input: rowStyles.input,
    confirmButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: theme.spacing.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
    },
    confirmSpacer: rowStyles.confirmSpacer,
    confirmedTag: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    advancedToggle: {
      marginTop: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
    },
    advancedToggleText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    output: {
      marginTop: theme.spacing.md,
      padding: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      gap: theme.spacing.xs,
    },
    outputTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
    },
    outputRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
    },
    outputLabel: {
      fontSize: theme.typography.fontSizeSm,
      flexShrink: 1,
    },
    outputValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    weighted: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
    },
    grid: {
      gap: theme.spacing.xxs,
    },
    gridTitle: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xxs,
    },
    gridRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    gridCorner: {
      width: theme.spacing.xxl,
    },
    gridHead: {
      width: theme.spacing.xxl,
      flexShrink: 0,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'right',
      paddingHorizontal: theme.spacing.xxs,
    },
    gridCell: {
      width: theme.spacing.xxl,
      flexShrink: 0,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'right',
      paddingHorizontal: theme.spacing.xxs,
    },
    disclaimer: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xs,
    },
    footerWrap: {
      gap: theme.spacing.xxs,
    },
    pendingStages: {
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'right',
      paddingHorizontal: theme.spacing.lg,
    },
    footer: {
      flexDirection: 'row',
      gap: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    closeButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    closeButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    footerButton: {
      flex: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.primary,
    },
    actionDisabled: {
      opacity: 0.5,
    },
    footerButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
